import { toBooleanFlag } from '@/utils/dataNormalization';

export const normalizePaymentPlanStatusCode = (value) => {
  const normalized = String(value || '').trim().toUpperCase();

  if (normalized === 'DONE' || normalized === 'PENDING') {
    return normalized;
  }

  if (['PAID', 'PAGADO'].includes(normalized)) {
    return 'DONE';
  }

  return 'PENDING';
};

export const normalizePaymentPlanDate = (value) => String(value || '').trim();

const normalizeEditMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['full', 'billing_locked', 'payment_locked'].includes(normalized)) {
    return normalized;
  }

  return 'full';
};

const resolveLocalEditMode = (row = {}) => {
  const hasBillingRelation = Number(row?.invoice_id || 0) > 0
    || Number(row?.simple_note_id || 0) > 0
    || toBooleanFlag(row?.invoiced)
    || String(row?.invoice || row?.invoice_reference || '').trim() !== ''
    || String(row?.simple_note_reference || '').trim() !== '';

  const hasPaymentRelation = String(row?.payment || row?.payment_reference || '').trim() !== ''
    || Number(row?.paid || 0) > 0
    || normalizePaymentPlanStatusCode(row?.payment_status_code || row?.payment_status) === 'DONE';

  if (!hasBillingRelation && !hasPaymentRelation) {
    return 'full';
  }

  if (hasPaymentRelation) {
    return 'payment_locked';
  }

  return 'billing_locked';
};

const resolveEditPermissions = (mode, raw = {}) => {
  const source = raw && typeof raw === 'object' ? raw : {};

  const defaults = {
    full: {
      can_edit_general: true,
      can_edit_billing: true,
      can_edit_amount: true,
      can_edit_payment_date: true,
      can_edit_details: true,
    },
    billing_locked: {
      can_edit_general: false,
      can_edit_billing: false,
      can_edit_amount: false,
      can_edit_payment_date: true,
      can_edit_details: true,
    },
    payment_locked: {
      can_edit_general: false,
      can_edit_billing: false,
      can_edit_amount: false,
      can_edit_payment_date: false,
      can_edit_details: true,
    },
  };

  const base = defaults[mode] || defaults.full;

  return {
    can_edit_general: 'can_edit_general' in source ? toBooleanFlag(source.can_edit_general) : base.can_edit_general,
    can_edit_billing: 'can_edit_billing' in source ? toBooleanFlag(source.can_edit_billing) : base.can_edit_billing,
    can_edit_amount: 'can_edit_amount' in source ? toBooleanFlag(source.can_edit_amount) : base.can_edit_amount,
    can_edit_payment_date: 'can_edit_payment_date' in source ? toBooleanFlag(source.can_edit_payment_date) : base.can_edit_payment_date,
    can_edit_details: 'can_edit_details' in source ? toBooleanFlag(source.can_edit_details) : base.can_edit_details,
  };
};

export const normalizePaymentPlanRow = (row = {}, index = 0) => {
  const statusCode = normalizePaymentPlanStatusCode(
    row.payment_status_code || row.status_code || row.payment_status
  );
  const special = Number(row.special || 0) === 1 ? 1 : 0;
  const voucherType = String(row.voucher_type || '').trim().toLowerCase() || (special === 1 ? 'simple_note' : 'cfdi');
  const editMode = normalizeEditMode(row.edit_mode || resolveLocalEditMode(row));
  const editPermissions = resolveEditPermissions(editMode, row.edit_permissions);
  const invoicePaymentMethodCode = String(
    row.invoice_billing_data?.payment_method?.code
    || row.invoice_payment_method
    || ''
  ).trim();
  const invoicePaymentMethodLabel = String(
    row.invoice_billing_data?.payment_method?.label
    || row.invoice_payment_method_label
    || ''
  ).trim();
  const invoiceContributorId = Number(
    row.invoice_billing_data?.contributor?.id
    || row.invoice_contributor_id
    || row.contributor_id
    || 0
  ) || 0;
  const invoiceContributorName = String(
    row.invoice_billing_data?.contributor?.name
    || row.invoice_contributor_name
    || ''
  ).trim();
  const invoiceBilledRfc = String(
    row.invoice_billing_data?.contributor?.rfc
    || row.invoice_billing_data?.rfc
    || row.invoice_billed_rfc
    || ''
  ).trim();

  return {
    id: Number(row.id || 0) || index + 1,
    position: index + 1,
    company_id: Number(row.company_id || row.customer_id || row.company?.id || row.customer?.id || 0) || 0,
    customer_id: Number(row.customer_id || row.company_id || row.customer?.id || row.company?.id || 0) || 0,
    company_name: String(row.company_name || row.customer_name || row.company?.name || row.customer?.name || '').trim(),
    customer_name: String(row.customer_name || row.company_name || row.customer?.name || row.company?.name || '').trim(),
    date: normalizePaymentPlanDate(row.date),
    concept: String(row.concept || '').trim() || '-',
    comments: String(row.comments || '').trim(),
    area_id: Number(row.area_id || 0) || 0,
    subtotal: Number(row.subtotal || 0) || 0,
    total: Number(row.total || 0) || 0,
    currency: String(row.currency || 'MXN').trim().toUpperCase() || 'MXN',
    exchange_rate: Number(row.exchange_rate || 1) || 1,
    taxes: row?.taxes && typeof row.taxes === 'object'
      ? row.taxes
      : { transferred_total: 0, withheld_total: 0, items: [] },
    special,
    voucher_type: voucherType,
    invoice_id: Number(row.invoice_id || 0) || 0,
    simple_note_id: Number(row.simple_note_id || 0) || 0,
    simple_note_date: normalizePaymentPlanDate(row.simple_note_date),
    invoice_date: normalizePaymentPlanDate(row.invoice_date),
    period: String(row.period || '').trim(),
    payment_receipt_id: Number(row.payment_receipt_id || row.payment_receipt?.id || 0) || 0,
    payment_receipt_code: String(row.payment_receipt_code || row.payment?.code || '').trim(),
    payment_reference: String(row.payment_reference || row.payment?.reference || '').trim(),
    tax_id: Number(row.tax_id || row.tax?.id || 0) || 0,
    tax_group_id: Number(row.tax_group_id || row.tax_group?.id || 0) || 0,
    simple_note_reference: String(row.simple_note_reference || '').trim(),
    invoice: String(row.invoice || '').trim(),
    contributor_id: invoiceContributorId,
    invoice_payment_method: invoicePaymentMethodCode,
    invoice_payment_method_label: invoicePaymentMethodLabel,
    invoice_contributor_id: invoiceContributorId,
    invoice_contributor_name: invoiceContributorName,
    invoice_billed_rfc: invoiceBilledRfc,
    invoice_billing_data: {
      payment_method: {
        code: invoicePaymentMethodCode,
        label: invoicePaymentMethodLabel,
      },
      contributor: {
        id: invoiceContributorId,
        name: invoiceContributorName,
        rfc: invoiceBilledRfc,
      },
      rfc: invoiceBilledRfc,
    },
    payment: String(row.payment || '').trim(),
    payment_status_code: statusCode,
    payment_status: String(row.payment_status || '').trim(),
    invoiced: row.invoiced === true || row.invoiced === 1,
    edit_mode: editMode,
    edit_permissions: editPermissions,
  };
};
